import {
  useCallback,
  useLayoutEffect,
  useRef,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type Ref,
} from "react";
import {
  DATE_INPUT_SEGMENTS,
  DATE_TIME_INPUT_SEGMENTS,
  applyDigitToDateSegment,
  formatPastedDate,
  getDateInputSegmentAtPosition,
  getDateInputSegmentSelection,
  normalizePastedDateSegment,
  replaceDateInputSegment,
  splitDateInputValue,
  type DateInputSegment,
  type DateInputSelection,
} from "@/components/dateTime/segmentedDateInputUtils";

interface UseSegmentedDateInputOptions {
  inputValue: string;
  enabled?: boolean;
  includeTimeSegments?: boolean;
  onValueChange: (
    value: string,
    edit: { segment: DateInputSegment | null; complete: boolean },
  ) => void;
  externalInputRef?: Ref<HTMLInputElement>;
}

const assignRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  if (ref) {
    (ref as { current: T | null }).current = value;
  }
};

const isSelectionWithinSegment = (
  selectionStart: number,
  selectionEnd: number,
  segmentSelection: DateInputSelection,
): boolean =>
  selectionStart >= segmentSelection.start &&
  selectionEnd <= segmentSelection.end;

export default function useSegmentedDateInput({
  inputValue,
  enabled = true,
  includeTimeSegments = false,
  onValueChange,
  externalInputRef,
}: UseSegmentedDateInputOptions) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const activeSegmentRef = useRef<DateInputSegment | null>(null);
  const segmentBufferRef = useRef("");
  const pendingSelectionRef = useRef<DateInputSelection | null>(null);
  const inputSegments = includeTimeSegments
    ? DATE_TIME_INPUT_SEGMENTS
    : DATE_INPUT_SEGMENTS;

  const mergedInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element;
      assignRef(externalInputRef, element);
    },
    [externalInputRef],
  );

  const applySelection = useCallback((selection: DateInputSelection) => {
    inputRef.current?.setSelectionRange(selection.start, selection.end);
  }, []);

  const queueSelection = useCallback((selection: DateInputSelection) => {
    pendingSelectionRef.current = selection;
  }, []);

  useLayoutEffect(() => {
    const pendingSelection = pendingSelectionRef.current;
    if (!pendingSelection) {
      return;
    }

    applySelection(pendingSelection);
    pendingSelectionRef.current = null;
  }, [applySelection, inputValue]);

  const resetActiveSegment = useCallback(() => {
    activeSegmentRef.current = null;
    segmentBufferRef.current = "";
  }, []);

  const selectSegment = useCallback(
    (segment: DateInputSegment) => {
      const selection = getDateInputSegmentSelection(inputValue, segment);
      if (!selection) {
        resetActiveSegment();
        return;
      }

      activeSegmentRef.current = segment;
      segmentBufferRef.current = "";
      applySelection(selection);
    },
    [applySelection, inputValue, resetActiveSegment],
  );

  const resolveActiveSegment = useCallback((): DateInputSegment | null => {
    const input = inputRef.current;
    if (!input) {
      return null;
    }

    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? selectionStart;

    if (activeSegmentRef.current) {
      const activeSelection = getDateInputSegmentSelection(
        inputValue,
        activeSegmentRef.current,
      );
      if (
        activeSelection &&
        isSelectionWithinSegment(
          selectionStart,
          selectionEnd,
          activeSelection,
        )
      ) {
        return activeSegmentRef.current;
      }
    }

    for (const segment of inputSegments) {
      const segmentSelection = getDateInputSegmentSelection(
        inputValue,
        segment,
      );
      if (
        segmentSelection &&
        isSelectionWithinSegment(
          selectionStart,
          selectionEnd,
          segmentSelection,
        )
      ) {
        return segment;
      }
    }

    if (selectionStart === selectionEnd) {
      return getDateInputSegmentAtPosition(
        inputValue,
        selectionStart,
        inputSegments,
      );
    }

    return null;
  }, [inputSegments, inputValue]);

  const queueSegmentSelection = useCallback(
    (value: string, segment: DateInputSegment) => {
      const selection = getDateInputSegmentSelection(value, segment);
      if (selection) {
        queueSelection(selection);
      }
    },
    [queueSelection],
  );

  const moveAfterCompletedSegment = useCallback(
    (value: string, segment: DateInputSegment) => {
      const segmentIndex = inputSegments.indexOf(segment);
      const nextSegment = inputSegments[segmentIndex + 1];
      segmentBufferRef.current = "";

      if (nextSegment) {
        activeSegmentRef.current = nextSegment;
        queueSegmentSelection(value, nextSegment);
        return;
      }

      activeSegmentRef.current = null;
      queueSegmentSelection(value, segment);
    },
    [inputSegments, queueSegmentSelection],
  );

  const replaceSegment = useCallback(
    (segment: DateInputSegment, replacement: string, complete: boolean) => {
      const nextValue = replaceDateInputSegment(
        inputValue,
        segment,
        replacement,
      );
      if (nextValue === inputValue) {
        if (complete) {
          moveAfterCompletedSegment(nextValue, segment);
          const pendingSelection = pendingSelectionRef.current;
          if (pendingSelection) {
            applySelection(pendingSelection);
            pendingSelectionRef.current = null;
          }
        }
        return;
      }

      onValueChange(nextValue, { segment, complete });
      if (complete) {
        moveAfterCompletedSegment(nextValue, segment);
        return;
      }

      activeSegmentRef.current = segment;
      segmentBufferRef.current = replacement;
      queueSegmentSelection(nextValue, segment);
    },
    [
      inputValue,
      applySelection,
      moveAfterCompletedSegment,
      onValueChange,
      queueSegmentSelection,
    ],
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      void event;
      if (!enabled || !inputRef.current) {
        return;
      }

      const position = inputRef.current.selectionStart ?? 0;
      const segment = getDateInputSegmentAtPosition(
        inputValue,
        position,
        inputSegments,
      );
      if (segment) {
        selectSegment(segment);
      } else {
        resetActiveSegment();
      }
    },
    [enabled, inputSegments, inputValue, resetActiveSegment, selectSegment],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      void event;
      if (!enabled || !inputRef.current || !inputValue.includes(".")) {
        return;
      }

      const position = inputRef.current.selectionStart ?? 0;
      const segment =
        getDateInputSegmentAtPosition(inputValue, position, inputSegments) ??
        "day";
      selectSegment(segment);
    },
    [enabled, inputSegments, inputValue, selectSegment],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): boolean => {
      if (!enabled || event.ctrlKey || event.metaKey || event.altKey) {
        return false;
      }

      const segment = resolveActiveSegment();
      if (!segment) {
        return false;
      }

      const segmentIndex = inputSegments.indexOf(segment);
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const direction = event.key === "ArrowLeft" ? -1 : 1;
        const targetSegment = inputSegments[segmentIndex + direction];
        if (targetSegment) {
          event.preventDefault();
          selectSegment(targetSegment);
          return true;
        }
        return false;
      }

      if (event.key === "Tab") {
        const direction = event.shiftKey ? -1 : 1;
        const targetSegment = inputSegments[segmentIndex + direction];
        if (targetSegment) {
          event.preventDefault();
          selectSegment(targetSegment);
          return true;
        }
        resetActiveSegment();
        return false;
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        replaceSegment(segment, "", false);
        return true;
      }

      if (!/^\d$/.test(event.key)) {
        return false;
      }

      event.preventDefault();
      if (activeSegmentRef.current !== segment) {
        activeSegmentRef.current = segment;
        segmentBufferRef.current = "";
      }

      const result = applyDigitToDateSegment(
        segment,
        segmentBufferRef.current,
        event.key,
      );
      if (!result.accepted) {
        return true;
      }

      replaceSegment(segment, result.value, result.complete);
      return true;
    },
    [
      enabled,
      inputSegments,
      replaceSegment,
      resetActiveSegment,
      resolveActiveSegment,
      selectSegment,
    ],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLElement>): boolean => {
      if (!enabled || !inputRef.current) {
        return false;
      }

      const pastedValue = event.clipboardData.getData("text");
      const formattedDate = formatPastedDate(pastedValue);
      const selectionStart = inputRef.current.selectionStart ?? 0;
      const { datePart, suffix } = splitDateInputValue(inputValue);

      if (formattedDate && selectionStart <= datePart.length) {
        event.preventDefault();
        const nextValue = `${formattedDate}${suffix}`;
        resetActiveSegment();
        onValueChange(nextValue, { segment: null, complete: true });
        queueSelection({
          start: formattedDate.length,
          end: formattedDate.length,
        });
        return true;
      }

      const segment = resolveActiveSegment();
      if (!segment) {
        return false;
      }

      const normalizedSegment = normalizePastedDateSegment(
        segment,
        pastedValue.trim(),
      );
      if (!normalizedSegment) {
        event.preventDefault();
        return true;
      }

      event.preventDefault();
      replaceSegment(segment, normalizedSegment, true);
      return true;
    },
    [
      enabled,
      inputValue,
      onValueChange,
      queueSelection,
      replaceSegment,
      resetActiveSegment,
      resolveActiveSegment,
    ],
  );

  const handleCut = useCallback(
    (event: ClipboardEvent<HTMLElement>): boolean => {
      if (!enabled || !inputRef.current) {
        return false;
      }

      const selectionStart = inputRef.current.selectionStart ?? 0;
      const selectionEnd = inputRef.current.selectionEnd ?? selectionStart;
      if (selectionStart === 0 && selectionEnd === inputValue.length) {
        resetActiveSegment();
        return false;
      }

      const segment = resolveActiveSegment();
      if (!segment) {
        return false;
      }

      event.preventDefault();
      const selectedText = inputValue.slice(selectionStart, selectionEnd);
      event.clipboardData.setData("text", selectedText);
      replaceSegment(segment, "", false);
      return true;
    },
    [
      enabled,
      inputValue,
      replaceSegment,
      resetActiveSegment,
      resolveActiveSegment,
    ],
  );

  return {
    inputRef: mergedInputRef,
    handleClick,
    handleFocus,
    handleKeyDown,
    handlePaste,
    handleCut,
    resetActiveSegment,
  };
}
